import { addSlideFooter } from '../slide_footer';
import { TITLE_TEXT } from '../constants';

const GRID_COLS = 12;
// Usable width and bottom edge below the slide title (inches; default 10 x 5.625 layout). The
// content top is calculated per slide so long Narrative questions cannot overlap the first tree row.
const AREA = { bottom: 5.12, w: 9.5, x: 0.25 };
const H_GAP = 0.3;
const V_GAP = 0.22;
const ACCENT_W = 0.06; // colored left accent bar
const PAD = 0.14;
const ACCENT_COLOR = '20343A';
const LABEL_COLOR = '3C3C3C';
const BORDER_COLOR = 'D9D9D9';
const CONNECTOR_COLOR = 'C8C8C8';
const DESCRIPTION_LINE_H = 0.14;
const MIN_CARD_H = 0.9;
const METRIC_ROW_H = 0.16;
const TITLE_H = 0.22;
const TITLE_CHARS_PER_LINE = 80;
const TITLE_LINE_H = 0.25;
const TITLE_Y = 0.5;
const INSIGHT_CHARS_PER_LINE = 120;
const INSIGHT_LINE_H = 0.14;

const getDescriptionLineCount = (description = '') =>
  description ? Math.max(1, description.split('\n').length) : 0;

const getContentTop = ({ insight = '', slideTitle = '' }) => {
  const titleLineCount = Math.max(1, Math.ceil(String(slideTitle).length / TITLE_CHARS_PER_LINE));
  const insightLineCount = insight
    ? Math.max(1, Math.ceil(String(insight).length / INSIGHT_CHARS_PER_LINE))
    : 0;

  return TITLE_Y + titleLineCount * TITLE_LINE_H + insightLineCount * INSIGHT_LINE_H + 0.16;
};

const getDesiredCardHeight = (node) =>
  Math.max(
    MIN_CARD_H,
    0.12 +
      TITLE_H +
      getDescriptionLineCount(node.description) * DESCRIPTION_LINE_H +
      node.rows.length * METRIC_ROW_H
  );

/**
 * Creates a native (shape-based) tree slide from a `tree` export item — white cards with a colored
 * left accent, dark title, and dashed label/value rows, joined by elbow connectors. Positions come
 * from the flowing 12-column grid computed in mapTreeToExportItem (`col` + `gridSpan`). No DOM.
 *
 * @param {Array}  nodes       [{ nodeId, row, col, gridSpan, color, title, description, rows:[{left,right}] }]
 * @param {Array}  connectors  [{ from: nodeId, to: nodeId[] }]
 * @param {Object} pptx
 * @param {Array}  reportDateRange
 * @param {string} reportType
 * @param {string} reportUrl
 * @param {Object} shopperAttributes
 * @param {string} slideTitle
 * @param {string} title
 * @returns {Object}
 */
export const createTreeSlide = ({
  connectors = [],
  insight,
  nodes = [],
  pptx,
  reportDateRange,
  reportType,
  reportUrl,
  shopperAttributes,
  slideTitle,
  title,
}) => {
  const slide = pptx.addSlide();
  const resolvedTitle = slideTitle || title || '';
  slide.addText(resolvedTitle, { ...TITLE_TEXT, fontSize: 14 });

  const titleLineCount = Math.max(
    1,
    Math.ceil(String(resolvedTitle).length / TITLE_CHARS_PER_LINE)
  );
  const insightLineCount = insight
    ? Math.max(1, Math.ceil(String(insight).length / INSIGHT_CHARS_PER_LINE))
    : 0;
  if (insight) {
    slide.addText(insight, {
      color: '3C3C3C',
      fit: 'shrink',
      fontFace: 'Arial',
      fontSize: 7,
      h: insightLineCount * INSIGHT_LINE_H,
      margin: 0,
      valign: 'top',
      w: AREA.w,
      x: AREA.x,
      y: TITLE_Y + titleLineCount * TITLE_LINE_H,
    });
  }

  if (!nodes.length) {
    addSlideFooter({ pptx, reportDateRange, reportType, reportUrl, shopperAttributes, slide });
    return slide;
  }

  const contentTop = getContentTop({ insight, slideTitle: resolvedTitle });
  const colWidth = AREA.w / GRID_COLS;
  const rowNumbers = [...new Set(nodes.map((node) => node.row))].sort((a, b) => a - b);
  const desiredHeights = rowNumbers.map((row) =>
    Math.max(...nodes.filter((node) => node.row === row).map(getDesiredCardHeight))
  );
  const availableCardHeight = AREA.bottom - contentTop - V_GAP * (rowNumbers.length - 1);
  const heightScale = Math.min(
    1,
    availableCardHeight / desiredHeights.reduce((total, height) => total + height, 0)
  );
  const rowLayouts = rowNumbers.reduce((layouts, row, index) => {
    const previous = layouts[rowNumbers[index - 1]];
    layouts[row] = {
      h: desiredHeights[index] * heightScale,
      y: previous ? previous.y + previous.h + V_GAP : contentTop,
    };
    return layouts;
  }, {});

  const boxes = nodes.reduce((acc, node) => {
    acc[node.nodeId] = {
      h: rowLayouts[node.row].h,
      w: Math.max(1.2, node.gridSpan * colWidth - H_GAP),
      x: AREA.x + node.col * colWidth + H_GAP / 2,
      y: rowLayouts[node.row].y,
    };
    return acc;
  }, {});

  // Elbow connectors (down from parent, across, down to child), behind the cards.
  connectors.forEach(({ from, to = [] }) => {
    const parent = boxes[from];
    if (!parent) return;
    const px = parent.x + parent.w / 2;
    const py = parent.y + parent.h;
    const midY = py + V_GAP / 2;
    to.forEach((childId) => {
      const child = boxes[childId];
      if (!child) return;
      const cx = child.x + child.w / 2;
      const line = { color: CONNECTOR_COLOR, width: 1 };
      slide.addShape(pptx.shapes.LINE, {
        h: Math.max(0.01, midY - py),
        line,
        w: 0.001,
        x: px,
        y: py,
      });
      slide.addShape(pptx.shapes.LINE, {
        h: 0.001,
        line,
        w: Math.max(0.01, Math.abs(cx - px)),
        x: Math.min(px, cx),
        y: midY,
      });
      slide.addShape(pptx.shapes.LINE, {
        h: Math.max(0.01, child.y - midY),
        line,
        w: 0.001,
        x: cx,
        y: midY,
      });
    });
  });

  nodes.forEach((node) => {
    const box = boxes[node.nodeId];
    const innerW = box.w - PAD * 2 - ACCENT_W;
    const innerX = box.x + PAD + ACCENT_W;

    slide.addShape(pptx.shapes.ROUNDED_RECTANGLE, {
      ...box,
      fill: { color: 'FFFFFF' },
      line: { color: BORDER_COLOR, width: 1 },
      rectRadius: 0.04,
    });
    slide.addShape(pptx.shapes.RECTANGLE, {
      fill: { color: node.color },
      h: box.h - 0.04,
      line: { type: 'none' },
      w: ACCENT_W,
      x: box.x + 0.02,
      y: box.y + 0.02,
    });

    const descriptionLines = getDescriptionLineCount(node.description);
    const descriptionHeight = descriptionLines * DESCRIPTION_LINE_H * heightScale;
    const titleHeight = TITLE_H * heightScale;

    slide.addText(node.title, {
      align: 'left',
      bold: true,
      color: ACCENT_COLOR,
      fit: 'shrink',
      fontFace: 'Arial',
      fontSize: 8,
      h: titleHeight,
      valign: 'middle',
      w: innerW,
      x: innerX,
      y: box.y + 0.04,
    });

    if (node.description) {
      slide.addText(node.description, {
        align: 'left',
        color: '5A7E88',
        fit: 'shrink',
        fontFace: 'Arial',
        fontSize: 5,
        h: descriptionHeight,
        margin: 0,
        valign: 'top',
        w: innerW,
        x: innerX,
        y: box.y + 0.04 + titleHeight,
      });
    }

    // Rows are positioned (not addTable) with fit:'shrink', so they always stay inside the card.
    const rowsTop = box.y + 0.08 + titleHeight + descriptionHeight;
    const rowsHeight = Math.max(0, box.y + box.h - 0.04 - rowsTop);
    const rowHeight = node.rows.length ? rowsHeight / node.rows.length : 0;
    const labelW = innerW * 0.56;

    node.rows.forEach((row, index) => {
      const rowY = rowsTop + index * rowHeight;
      if (index > 0) {
        slide.addShape(pptx.shapes.LINE, {
          h: 0.001,
          line: { color: 'E6E6E6', dashType: 'dash', width: 0.5 },
          w: innerW,
          x: innerX,
          y: rowY,
        });
      }
      slide.addText(row.left, {
        align: 'left',
        color: LABEL_COLOR,
        fit: 'shrink',
        fontFace: 'Arial',
        fontSize: 6,
        h: rowHeight,
        margin: 0,
        valign: 'middle',
        w: labelW,
        x: innerX,
        y: rowY,
      });
      slide.addText(row.right, {
        align: 'right',
        bold: index === 0,
        color: ACCENT_COLOR,
        fit: 'shrink',
        fontFace: 'Arial',
        fontSize: 6,
        h: rowHeight,
        margin: 0,
        valign: 'middle',
        w: innerW - labelW,
        x: innerX + labelW,
        y: rowY,
      });
    });
  });

  addSlideFooter({ pptx, reportDateRange, reportType, reportUrl, shopperAttributes, slide });
  return slide;
};
